import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import GroupsIcon from '@mui/icons-material/Groups';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import BlockIcon from '@mui/icons-material/Block';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const STATUS_META = {
  pending: {
    label: 'Pending',
    borderColor: 'grey.400',
    icon: <InfoOutlinedIcon fontSize="small" />,
    chipColor: 'default',
    tooltip: 'Ready for execution',
  },
  unedited: {
    label: 'Unedited',
    borderColor: 'grey.300',
    icon: <InfoOutlinedIcon fontSize="small" />,
    chipColor: 'default',
    tooltip: 'No criteria defined',
  },
  edited: {
    label: 'Edited',
    borderColor: 'info.main',
    icon: <EditNoteIcon fontSize="small" />,
    chipColor: 'info',
    tooltip: 'Criteria defined (not executed)',
  },
  executing: {
    label: 'Executing',
    borderColor: 'warning.main',
    icon: <CircularProgress size={16} />,
    chipColor: 'warning',
    tooltip: 'Calculation in progress',
  },
  executed: {
    label: 'Executed',
    borderColor: 'success.main',
    icon: <CheckCircleIcon fontSize="small" />,
    chipColor: 'success',
    tooltip: 'Patient count calculated',
  },
  completed: {
    label: 'Completed',
    borderColor: 'success.main',
    icon: <CheckCircleIcon fontSize="small" />,
    chipColor: 'success',
    tooltip: 'Execution completed',
  },
  error: {
    label: 'Error',
    borderColor: 'error.main',
    icon: <ErrorOutlineIcon fontSize="small" />,
    chipColor: 'error',
    tooltip: 'Execution failed',
  },
};

const CohortNode = ({
  id,
  label,
  criteria,
  criteriaCount,
  patientCount,
  isExecuted,
  status = 'unedited',
  isBreakpoint = false,
  selected = false,
  compact = false,
  onEdit,
  onDelete,
  onAddChild,
  onInsert,
  onExecuteToHere,
  onToggleBreakpoint,
  onRename,
}) => {
  const meta = STATUS_META[status] || STATUS_META.unedited;

  const executed = status === 'executed' || status === 'completed' || isExecuted === true;

  const includeCount = useMemo(() => {
    if (typeof criteriaCount?.include === 'number') return criteriaCount.include;
    if (Array.isArray(criteria?.include)) return criteria.include.length;
    return undefined;
  }, [criteria, criteriaCount]);

  const excludeCount = useMemo(() => {
    if (typeof criteriaCount?.exclude === 'number') return criteriaCount.exclude;
    if (Array.isArray(criteria?.exclude)) return criteria.exclude.length;
    return undefined;
  }, [criteria, criteriaCount]);

  const criteriaSummaryText = useMemo(() => {
    if (typeof includeCount === 'number' || typeof excludeCount === 'number') {
      const inc = typeof includeCount === 'number' ? includeCount : 0;
      const exc = typeof excludeCount === 'number' ? excludeCount : 0;
      return `${inc} Include, ${exc} Exclude`;
    }
    return 'No criteria defined';
  }, [excludeCount, includeCount]);

  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [draftLabel, setDraftLabel] = useState(label || '');
  const [contextMenuPosition, setContextMenuPosition] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraftLabel(label || '');
  }, [label]);

  useEffect(() => {
    if (isEditingLabel && inputRef.current) {
      inputRef.current.focus();
      try {
        inputRef.current.select();
      } catch {
        // ignore
      }
    }
  }, [isEditingLabel]);

  const commitLabel = () => {
    const next = (draftLabel || '').trim();
    if (!next) {
      setDraftLabel(label || '');
      setIsEditingLabel(false);
      return;
    }

    if (next !== label) {
      onRename?.(id, next);
    }
    setIsEditingLabel(false);
  };

  // ReactFlow uses pointer/mouse down to start drag/pan. We must stop propagation early
  // for interactive UI elements inside the node, otherwise clicks can be swallowed.
  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const stopAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const triggerAction = (e, action) => {
    stopAll(e);
    action?.(id);
  };

  const closeContextMenu = useCallback(() => {
    setContextMenuPosition(null);
  }, []);

  const openContextMenuAt = (x, y) => {
    setContextMenuPosition({ top: y, left: x });
  };

  const handleOpenContextMenuFromPointer = (e) => {
    stopAll(e);
    openContextMenuAt(e.clientX + 2, e.clientY - 4);
  };

  const handleOpenContextMenuFromButton = (e) => {
    stopAll(e);
    const rect = e.currentTarget?.getBoundingClientRect?.();
    if (rect) {
      openContextMenuAt(rect.left + rect.width / 2, rect.bottom + 6);
      return;
    }
    openContextMenuAt(e.clientX + 2, e.clientY - 4);
  };

  const handleOpenContextMenuFromKeyboard = (e) => {
    stopAll(e);
    const rect = e.currentTarget?.getBoundingClientRect?.();
    if (rect) {
      openContextMenuAt(rect.left + rect.width / 2, rect.top + rect.height / 2);
      return;
    }
    openContextMenuAt(0, 0);
  };

  const runMenuAction = (e, action) => {
    stopAll(e);
    closeContextMenu();
    action?.(id);
  };

  return (
    <>
      <Card
        onClick={() => onEdit?.(id)}
        onContextMenu={compact ? undefined : handleOpenContextMenuFromPointer}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            stopAll(e);
            onEdit?.(id);
            return;
          }

          if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
            handleOpenContextMenuFromKeyboard(e);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Cohort node ${label || id}. ${criteriaSummaryText}.`}
        sx={{
          width: compact ? 220 : 280,
          borderRadius: 2,
          border: '1px solid',
          borderColor: selected ? 'primary.main' : meta.borderColor,
          transition: 'transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
          boxShadow: selected ? '0 10px 24px rgba(0,0,0,0.12)' : '0 2px 10px rgba(0,0,0,0.06)',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: selected ? '0 12px 28px rgba(0,0,0,0.16)' : '0 8px 22px rgba(0,0,0,0.12)',
            borderColor: selected ? 'primary.main' : meta.borderColor,
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
          userSelect: 'none',
        }}
      >
        <Box sx={{ px: 1.25, pt: 1.25, pb: compact ? 1 : 0.75 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Tooltip title={meta.tooltip}>
                <Box
                  sx={{
                    color: selected ? 'primary.main' : meta.borderColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {meta.icon}
                </Box>
              </Tooltip>

              <Box sx={{ minWidth: 0 }}>
                {isEditingLabel ? (
                  <TextField
                    inputRef={inputRef}
                    className="nodrag"
                    value={draftLabel}
                    onChange={(e) => setDraftLabel(e.target.value)}
                    onMouseDown={stopPropagation}
                    onPointerDown={stopPropagation}
                    onClick={stopAll}
                    onDoubleClick={stopAll}
                    onBlur={commitLabel}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        stopAll(e);
                        commitLabel();
                      }
                      if (e.key === 'Escape') {
                        stopAll(e);
                        setDraftLabel(label || '');
                        setIsEditingLabel(false);
                      }
                    }}
                    variant="standard"
                    fullWidth
                    size="small"
                  />
                ) : (
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.15,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: compact ? 140 : 190,
                      cursor: 'default',
                    }}
                    title={label}
                    onClick={stopAll}
                    onDoubleClick={(e) => {
                      stopAll(e);
                      setIsEditingLabel(true);
                    }}
                    onMouseDown={stopPropagation}
                    onPointerDown={stopPropagation}
                  >
                    {label || 'Unnamed cohort'}
                  </Typography>
                )}

                {!compact ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {criteriaSummaryText}
                  </Typography>
                ) : null}
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.25} alignItems="center" className="nodrag">
              {isBreakpoint ? (
                <Tooltip title='Execution stops at this node when using "Execute to Breakpoint".'>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    icon={<OutlinedFlagIcon sx={{ fontSize: 16 }} />}
                    label="Breakpoint"
                    sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
              ) : null}

              <Chip size="small" label={meta.label} color={meta.chipColor} variant="outlined" sx={{ fontWeight: 700 }} />

              {executed && typeof patientCount === 'number' ? (
                <Chip
                  size="small"
                  icon={<GroupsIcon sx={{ fontSize: 16 }} />}
                  label={patientCount.toLocaleString()}
                  sx={{ fontWeight: 800 }}
                />
              ) : null}
            </Stack>
          </Stack>

          {!compact ? (
            <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} className="nodrag">
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  aria-label={`Edit node ${label || id}`}
                  className="nodrag"
                  onPointerDown={(e) => triggerAction(e, onEdit)}
                  onClick={stopAll}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') triggerAction(e, onEdit);
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add child node">
                <IconButton
                  size="small"
                  aria-label={`Add child node for ${label || id}`}
                  className="nodrag"
                  onPointerDown={(e) => triggerAction(e, onAddChild)}
                  onClick={stopAll}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') triggerAction(e, onAddChild);
                  }}
                >
                  <AddCircleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Insert node between parent and this node">
                <IconButton
                  size="small"
                  aria-label={`Insert node before ${label || id}`}
                  className="nodrag"
                  onPointerDown={(e) => triggerAction(e, onInsert)}
                  onClick={stopAll}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') triggerAction(e, onInsert);
                  }}
                >
                  <CallSplitIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Execute to here">
                <IconButton
                  size="small"
                  aria-label={`Execute graph to node ${label || id}`}
                  className="nodrag"
                  onPointerDown={(e) => triggerAction(e, onExecuteToHere)}
                  onClick={stopAll}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') triggerAction(e, onExecuteToHere);
                  }}
                >
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  aria-label={`Delete node ${label || id}`}
                  className="nodrag"
                  onPointerDown={(e) => triggerAction(e, onDelete)}
                  onClick={stopAll}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') triggerAction(e, onDelete);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Node actions menu">
                <IconButton
                  size="small"
                  aria-label={`Open node menu for ${label || id}`}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(contextMenuPosition)}
                  className="nodrag"
                  onPointerDown={stopPropagation}
                  onClick={handleOpenContextMenuFromButton}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleOpenContextMenuFromButton(e);
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : null}
        </Box>

        {!compact ? (
          <>
            <Divider />
            <CardContent sx={{ pt: 1.25, pb: 1.25 }}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap">
                {typeof includeCount === 'number' ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<PlaylistAddCheckIcon sx={{ fontSize: 16 }} />}
                    label={`${includeCount} include`}
                  />
                ) : null}
                {typeof excludeCount === 'number' ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<BlockIcon sx={{ fontSize: 16 }} />}
                    label={`${excludeCount} exclude`}
                  />
                ) : null}
                {typeof includeCount !== 'number' && typeof excludeCount !== 'number' ? (
                  <Typography variant="caption" color="text.secondary">
                    No criteria yet. Double-click the title to rename.
                  </Typography>
                ) : null}
              </Stack>
            </CardContent>
          </>
        ) : null}
      </Card>

      <Menu
        open={Boolean(contextMenuPosition)}
        onClose={closeContextMenu}
        MenuListProps={{
          'aria-label': `Actions for node ${label || id}`,
        }}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenuPosition
            ? { top: contextMenuPosition.top, left: contextMenuPosition.left }
            : undefined
        }
      >
        <MenuItem aria-label={`Edit node ${label || id}`} onClick={(e) => runMenuAction(e, onEdit)} disabled={!onEdit}>
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Node</ListItemText>
        </MenuItem>
        <MenuItem
          aria-label={`Add child node for ${label || id}`}
          onClick={(e) => runMenuAction(e, onAddChild)}
          disabled={!onAddChild}
        >
          <ListItemIcon>
            <AddCircleOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add Child</ListItemText>
        </MenuItem>
        <MenuItem aria-label={`Delete node ${label || id}`} onClick={(e) => runMenuAction(e, onDelete)} disabled={!onDelete}>
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Node</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          aria-label={isBreakpoint ? 'Remove breakpoint' : 'Set breakpoint'}
          onClick={(e) => runMenuAction(e, onToggleBreakpoint)}
          disabled={!onToggleBreakpoint}
        >
          <ListItemIcon>
            <OutlinedFlagIcon fontSize="small" color={isBreakpoint ? 'warning' : 'inherit'} />
          </ListItemIcon>
          <ListItemText>{isBreakpoint ? 'Remove Breakpoint' : 'Set as Breakpoint'}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default React.memo(CohortNode);
